import { sha256 } from '../lib/utils/createHashUtils'
import watch from 'redux-watch'

import {
  transactionComplete,
  transactionEvent,
  transactionFailure,
  transactionStart,
  transactionUpdate,
} from '../actions/AnalyticsActions'
import { AppStore } from 'src/client/store'
import { BuyerType } from 'reducers/analytics'
import { reportClientError } from 'services/errorReportingService'
import { getOrderWithPaymentsAndReservations } from 'api/order'
import { fireInteractionEvent } from 'api/googleTagManager'
import { insurancePurchased } from 'analytics/eventDefinitions'

export function watchAnalytics(store: AppStore, dataLayer: any) {
  setDataLayerDetails(store, dataLayer)

  store.subscribe(
    watch(store.getState, 'analytics.transaction')(() => {
      try {
        const { analytics, auth, geo } = store.getState()
        const {
          cartSuccess,
          orderSuccess,
          eventComplete,
          cart,
          order,
          isNewOrder,
        } = analytics.transaction

        const orderIdValid = order.id === cart.order?.id
        const orderComplete = cartSuccess && orderSuccess && orderIdValid
        if (orderComplete) {
          // Avoid duplicate tracking of a single event
          if (eventComplete) {
            return
          }

          store.dispatch(transactionEvent({
            auth,
            geo,
            isNewOrder,
            ...analytics.transaction,
          }))

          if (order.insuranceItems?.length > 0) {
            fireInteractionEvent(insurancePurchased())
          }
        }
      } catch (e) {
        // don't bubble up to app. we don't want it failing due to this
        // but we do want to report it to sentry so we be aware of it
        reportClientError(e)
      }
    }),
  )
  store.subscribe(
    watch(store.getState, 'cart')(() => {
      const { cart } = store.getState()

      // bad data was causing analytics actions to throw errors
      // and crash the app. So wrap in a try/catch and ignore errors
      try {
        if (cart.processing) {
          store.dispatch(transactionStart(cart))
        }
        if (cart.processed) {
          store.dispatch(transactionUpdate(cart))
          getTransactionOrder(store, cart.order.id)
        }
      } catch (e) {
        // don't bubble up to app. we don't want it failing due to this
        reportClientError(e)
      }
    }),
  )
}

function getTransactionOrder(store: AppStore, orderId: string) {
  return getOrderWithPaymentsAndReservations(orderId)
    .then(order => store.dispatch(transactionComplete(order)))
    .catch(error => store.dispatch(transactionFailure(error)))
}

function setDataLayerDetails(store: AppStore, dataLayer: any) {
  const { auth, geo, utm, config } = store.getState() as App.State
  const details = {
    hitType: 'dataLayer-initialized',
    event: 'dataLayer-initialized',
    pageDetails: {
      siteVersion: '2.0',
    },
    sessionDetails: {
      loggedIn: auth.account.memberId ? 'loggedIN' : 'loggedOUT',
      selectedLocation: geo.currentRegionCode,
      utm,
      headlessMode: config.headlessMode,
      startLoggedIn: !!auth.account.memberId,
    },
    userDetails: {
      userID: auth.account.memberId || '',
      userEmailRaw: auth.account.email || '',
      userEmail: sha256(auth.account.email) || '',
      personContactID: auth.account.personContactId || '',
      buyerType: auth.account.total_purchase_count > 0 ? BuyerType.existing : BuyerType.new,
      utmSource: auth.account.utmSource || '',
      utmMedium: auth.account.utmMedium || '',
      utmCampaign: auth.account.utmCampaign || '',
      utmContent: auth.account.utmContent || '',
      utmTerm: auth.account.utmTerm || '',
      utmAdgroup: auth.account.utmAdgroup || '',
    },
  }
  dataLayer.push(details)
}
